import {
  Breakpoints as BreakpointsType,
  DefaultTheme,
  Palette,
  PaletteVariants,
  Sizes,
} from 'styled-components';
import { tinkBlue } from '../../../Theme/colors/tinkBlue';
import { tinkGrey } from '../../../Theme/colors/tinkGrey';
import { tinkGreen } from '../../../Theme/colors/tinkGreen';
import { tinkIncome } from '../../../Theme/colors/tinkIncome';
import { tinkMetal } from '../../../Theme/colors/tinkMetal';
import { tinkRed } from '../../../Theme/colors/tinkRed';
import { tinkSalmon } from '../../../Theme/colors/tinkSalmon';
import { tinkYellow } from '../../../Theme/colors/tinkYellow';

declare module 'styled-components' {
  export type Devices =
    | 'mobileS'
    | 'mobileM'
    | 'mobileL'
    | 'tablet'
    | 'laptop'
    | 'laptopM'
    | 'laptopL'
    | 'desktop';

  export type Sizes =
    | 'auto'
    | 'none'
    | 'xxxsmall'
    | 'xxsmall'
    | 'xsmall'
    | 'small'
    | 'normal'
    | 'normedium'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge'
    | 'xxxlarge';

  export type WidthSizes =
    | Extract<Sizes, 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'>
    | 'modal'
    | 'article'
    | 'content'
    | 'site'
    | 'drawer';

  export type RoundedCornerSizes =
    | Extract<Sizes, 'xsmall' | 'small' | 'medium' | 'large'>
    | 'huge'
    | 'pill'
    | 'rounded'
    | 'sharp';

  export type ShadowSizes =
    | Extract<Sizes, 'small' | 'medium' | 'large'>
    | 'hover'
    | 'huge'
    | 'giga'
    | 'topbar';

  export type Breakpoints = { [key in Devices]: number };

  export type PaletteVariants =
    | '50'
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900'
    | 'A100'
    | 'A200'
    | 'A400'
    | 'A700'
    | 'contrastDefaultColor';

  export type Palette =
    | 'black'
    | 'blue'
    | 'error'
    | 'grey'
    | 'green'
    | 'greyBlack'
    | 'greyBorder'
    | 'greyLight'
    | 'greySoft'
    | 'greyWhite'
    | 'metal'
    | 'primary'
    | 'red'
    | 'secondary'
    | 'transferGrey'
    | 'white'
    | 'yellow';

  export type BorderRadius = 'pill' | 'rounded' | 'sharp';

  export type TextWeight = 'BOLD' | 'NORMAL';

  export interface DefaultTheme {
    device: { [key in Devices]: string };
    deviceMaxWidth: { [key in Exclude<Devices, 'laptopM'>]: string };
    font: {
      face: string;
      weights: {
        regular: number;
        semiBold: number;
      };
      monospace: string;
    };
    palette: {
      [key in Palette]: {
        main: string;
      } & { [variant in PaletteVariants]?: string };
    };
    roundedCorners: { [key in RoundedCornerSizes]: string };
    shadows: { [key in ShadowSizes]: string };
    sizes: { [key in Sizes]: string };
    widths: { [key in WidthSizes]: string };
  }
}

export type TinkFontSizes = Sizes;

export type TinkPaletteColors = Palette;

export type TinkPaletteVariants = PaletteVariants;

export const Breakpoints: BreakpointsType = {
  mobileS: 320,
  mobileM: 375,
  mobileL: 425,
  tablet: 768,
  // Chrome will set this value when "Request desktop site" is checked.
  laptop: 980,
  laptopM: 1260,
  laptopL: 1440,
  desktop: 2560,
};

const createBreakpoint = (width: number) => `(min-width: ${width}px)`;
const createMaxWidthBreakpoint = (width: number) => `(max-width: ${width}px)`;

export const DEFAULT_FONT_FAMILY = '"Lota Grotesque", Avenir, Arial, sans-serif';

export const theme: DefaultTheme = {
  device: {
    mobileS: createBreakpoint(Breakpoints.mobileS),
    mobileM: createBreakpoint(Breakpoints.mobileM),
    mobileL: createBreakpoint(Breakpoints.mobileL),
    tablet: createBreakpoint(Breakpoints.tablet),
    laptop: createBreakpoint(Breakpoints.laptop),
    laptopM: createBreakpoint(Breakpoints.laptopM),
    laptopL: createBreakpoint(Breakpoints.laptopL),
    desktop: createBreakpoint(Breakpoints.desktop),
  },
  deviceMaxWidth: {
    mobileS: createMaxWidthBreakpoint(Breakpoints.mobileS),
    mobileM: createMaxWidthBreakpoint(Breakpoints.mobileM),
    mobileL: createMaxWidthBreakpoint(Breakpoints.mobileL),
    tablet: createMaxWidthBreakpoint(Breakpoints.tablet),
    laptop: createMaxWidthBreakpoint(Breakpoints.laptop),
    laptopL: createMaxWidthBreakpoint(Breakpoints.laptopL),
    desktop: createMaxWidthBreakpoint(Breakpoints.desktop),
  },
  font: {
    face: DEFAULT_FONT_FAMILY,
    weights: {
      regular: 400,
      semiBold: 600,
    },
    //  This is due to messed up behaviour with monospace font
    monospace: 'Menlo, Consolas, monospace,monospace',
  },
  palette: {
    black: {
      main: tinkGrey[800],
    },
    blue: {
      ...tinkBlue,
      main: tinkBlue[800],
    },
    error: {
      ...tinkRed,
      main: tinkRed[700],
    },
    green: {
      ...tinkIncome,
      main: tinkIncome[600],
    },
    grey: {
      ...tinkGrey,
      main: tinkGrey[500],
    },
    greyWhite: {
      main: tinkGrey[200],
    },
    greySoft: {
      main: tinkGrey[300],
    },
    greyBorder: {
      main: tinkGrey[400],
    },
    greyLight: {
      main: tinkGrey[500],
    },
    greyBlack: {
      main: tinkGrey[600],
    },
    transferGrey: {
      main: tinkGrey[700],
    },
    metal: {
      ...tinkMetal,
      main: tinkMetal[500],
    },
    primary: {
      ...tinkGreen,
      main: tinkGreen[600],
    },
    red: {
      ...tinkRed,
      main: tinkRed[700],
    },
    secondary: {
      ...tinkSalmon,
      main: tinkSalmon[700],
    },
    white: {
      main: '#FFFFFF',
    },
    yellow: {
      ...tinkYellow,
      main: tinkYellow[700],
    },
  },
  roundedCorners: {
    huge: '100%',
    large: '1rem',
    medium: '0.5rem',
    small: '0.375rem',
    xsmall: '0.25rem',
    pill: '4.5rem',
    rounded: '0.5rem',
    sharp: '0rem',
  },
  shadows: {
    giga: '0px 12px 32px rgba(0, 0, 0, 0.12)',
    hover: '0px 6px 12px 0 rgba(0,0,0,0.12)',
    huge: '0px 8px 18px 0 rgba(0,0,0,0.08)',
    large: '0px 6px 12px 0 rgba(0,0,0,0.08)',
    medium: '0px 4px 8px 0 rgba(0,0,0,0.08)',
    small: '0px 2px 4px 0 rgba(0,0,0,0.08)',
    topbar: '0 0 10px 0 rgba(0,0,0,0.13)',
  },
  sizes: {
    auto: 'auto',
    none: '0',
    xxxsmall: '0.25rem', // 4px
    xxsmall: '0.5rem', // 8px
    xsmall: '0.75rem', // 12px
    small: '0.875rem', // 14px
    normal: '1rem', // 16px
    normedium: '1.25rem', // 20px
    medium: '1.5rem', // 24px
    large: '2rem', // 32px
    xlarge: '2.5rem', // 40px
    xxlarge: '4rem', // 64px
    xxxlarge: '5rem', // 80px
  },
  widths: {
    xxsmall: '25rem', // 400px  (4 column in design is 350)
    xsmall: '30rem', // 480px  (5 column in design is 445)
    small: '35rem', // 560px  (6 column in design is 540)
    medium: '40rem', // 640px  (7 column in design is 635)
    large: '45rem', // 720px  (8 column in design is 730)
    xlarge: '50rem', // 800px  (9 column in design is 825)
    xxlarge: '70rem',
    modal: '33.75rem',
    article: '60rem', // 960px article width
    content: '80rem', // 1200px content max width
    site: '95rem', // 1440px is the max width of the site
    drawer: '15.625rem', // 250px width of console sidebar on laptop
  },
};

export type TinkTheme = DefaultTheme;
